import { useState, useEffect, useMemo, useContext } from 'react';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { accountService } from '../services/accountService';
import Layout from '../components/Layout';
import MaxBadge from '../components/MaxBadge';
import UpgradeModal from '../components/UpgradeModal';
import ProviderDetailModal, { ProviderStats } from '../components/ProviderDetailModal';
import { AI_PROVIDERS, AiProvider, providerColor } from '../constants/aiProviders';
import { FORM_FACTORS, FORM_FACTOR_META } from '../constants/clientSurfaces';

interface ProvidersReport {
  scope: 'user' | 'company';
  userCount: number;
  activeUsers: number;
  days: number;
  allTime?: { totalPrompts: number; monthlyPrompts: number } | null;
  totals: {
    prompts: number;
    sessions: number;
    tokensIn: number;
    tokensOut: number;
    files: number;
    protectedItems: number;
  };
  providers: ProviderStats[];
}

const shortDate = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/** Hover layer for the activity stream: per-provider rows (zeros hidden), total on top. */
function StreamTooltip({ active, payload, label }: any) {
  if (!active || !payload || payload.length === 0) return null;
  const rows = payload
    .filter((entry: any) => Number(entry.value) > 0)
    .sort((a: any, b: any) => Number(b.value) - Number(a.value));
  const total = payload.reduce((sum: number, entry: any) => sum + (Number(entry.value) || 0), 0);
  return (
    <div
      className="rounded-lg border px-3 py-2 text-xs shadow-lg"
      style={{ background: 'var(--tooltip-bg)', borderColor: 'var(--tooltip-border)', color: 'var(--color-text)' }}
    >
      <p className="font-semibold mb-1">{shortDate(String(label))}</p>
      {rows.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}><Trans>No prompts</Trans></p>
      ) : (
        <div className="space-y-0.5">
          {rows.map((entry: any) => (
            <div key={entry.dataKey} className="flex items-center justify-between gap-4">
              <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--color-text-secondary)' }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                {entry.name}
              </span>
              <span className="tabular-nums font-medium">{Number(entry.value).toLocaleString()}</span>
            </div>
          ))}
          <div
            className="flex items-center justify-between gap-4 pt-1 mt-1 border-t font-semibold"
            style={{ borderColor: 'var(--tooltip-border)' }}
          >
            <span><Trans>Total</Trans></span>
            <span className="tabular-nums">{total.toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useLingui();
  const [account, setAccount] = useState<any>(null);
  const [company, setCompany] = useState<any>(null);
  const [usage, setUsage] = useState<any[]>([]);
  const [usageDays, setUsageDays] = useState<number>(30);
  const [providersReport, setProvidersReport] = useState<ProvidersReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [reportProvider, setReportProvider] = useState<AiProvider | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadDashboardData();
  }, [user, navigate]);

  // EPIC-15 (PLG): the free tier sees the last 24 hours only. The server
  // clamps regardless; requesting 1 day keeps the UI honest about what it
  // shows. Max keeps the picker's full ranges.
  const isMaxTier = account?.effectiveTier === 'max';
  const effectiveUsageDays = isMaxTier ? usageDays : 1;

  useEffect(() => {
    if (loading || !user) return;
    accountService.getUsage(effectiveUsageDays)
      .then((u) => setUsage(u))
      .catch((err) => console.error('Failed to refetch usage:', err));
    accountService.getUsageProviders(effectiveUsageDays)
      .then((report) => setProvidersReport(report))
      .catch((err) => console.error('Failed to fetch provider report:', err));
  }, [effectiveUsageDays, loading, user]);

  const loadDashboardData = async () => {
    try {
      const accountData = await accountService.getAccountStatus();
      setAccount(accountData);

      try {
        const companyData = await accountService.getCompany();
        if (companyData) {
          setCompany(companyData);
        }
      } catch (error: any) {
        if (error.response?.status !== 404) {
          console.error('Failed to load company data:', error);
        }
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysRemaining = () => {
    if (!account?.trialEndDate) return null;
    try {
      let endDate: Date;
      if (typeof account.trialEndDate === 'string') {
        endDate = new Date(account.trialEndDate);
      } else if (account.trialEndDate && typeof account.trialEndDate === 'object') {
        const dt = account.trialEndDate;
        endDate = new Date(
          dt.year || new Date().getFullYear(),
          (dt.month || 1) - 1,
          dt.day || 1,
          dt.hour || 0,
          dt.minute || 0,
          dt.second || 0
        );
      } else {
        return null;
      }
      const now = new Date();
      const diff = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 0;
    } catch (e) {
      console.error('Error calculating days remaining:', e, account.trialEndDate);
      return null;
    }
  };

  const n = (v: unknown) => Number(v) || 0;
  const chartData = usage.length > 0 ? usage : [];

  // Prompts by kind of device over the selected window.
  //
  // `platform` on an audit row says which AI the traffic went to; it never said
  // which PrivacyPal surface protected it, and the two mobile apps made that
  // the question people actually ask. The API answers it with
  // `promptsBySurface`, whose buckets are built to sum to the day's prompt
  // total, so this strip can never disagree with the chart above it.
  //
  // Only buckets that saw traffic are rendered: an account with no phones
  // should not be shown a permanent "Mobile 0".
  const surfaceTotals = FORM_FACTORS.map((key) => ({
    meta: FORM_FACTOR_META[key],
    prompts: chartData.reduce(
      (sum: number, day: any) => sum + n(day?.promptsBySurface?.[key]),
      0,
    ),
  })).filter((row) => row.prompts > 0);

  const surfacePromptTotal = surfaceTotals.reduce((sum, row) => sum + row.prompts, 0);

  const daysRemaining = getDaysRemaining();
  // Scope-consistent headline counter: the provider report's all-time count
  // spans the whole company on Max; the account's own counter is the fallback.
  const totalPrompts = Number(providersReport?.allTime?.totalPrompts ?? account?.totalPrompts) || 0;
  const effectiveStatus = account?.effectiveStatus || account?.status || 'inactive';
  const isPro = effectiveStatus === 'pro';
  const isTrial = effectiveStatus === 'trial';
  const isMax = account?.effectiveTier === 'max';

  // Max tier reports across the whole company; Pro reports the signed-in user
  // only. The server decides (usage + provider endpoints share the scope) —
  // the flags here only drive labels.
  const isCompanyScope = providersReport?.scope === 'company';
  const scopeUserCount = providersReport?.userCount || 1;

  // Payment failure banner (EPIC-07 7.5): admins see it when the account or
  // company subscription is past_due or unpaid. Stripe retries then cancels;
  // this is the window where updating the card saves the subscription.
  const failedStatuses = ['past_due', 'unpaid'];
  const paymentFailed =
    !!account?.isAdmin &&
    (failedStatuses.includes(account?.subscriptionStatus) ||
      failedStatuses.includes(account?.company?.subscriptionStatus) ||
      failedStatuses.includes(company?.subscriptionStatus));

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const monthName = now.toLocaleDateString(undefined, { month: 'long' });

  // Prompts across every supported provider for the current calendar month.
  // The scoped server-side count is authoritative (full month, company-wide on
  // Max); summing the visible window is the fallback.
  const windowMonthPrompts = chartData.reduce((sum, data) => {
    if (!data.date) return sum;
    let dataDate: Date;
    if (typeof data.date === 'string' && data.date.match(/^\d{4}-\d{2}-\d{2}/)) {
      dataDate = new Date(data.date + 'T00:00:00');
    } else {
      dataDate = new Date(data.date);
    }
    if (isNaN(dataDate.getTime())) return sum;
    if (dataDate.getFullYear() === currentYear && dataDate.getMonth() === currentMonth) {
      return n(sum) + AI_PROVIDERS.reduce((daySum, p) => daySum + n(data[p.key]), 0);
    }
    return sum;
  }, 0);
  const currentMonthPrompts = Number(providersReport?.allTime?.monthlyPrompts ?? windowMonthPrompts) || 0;

  const totalTokensIn = chartData.reduce((sum, data) => n(sum) + n(data.tokensIn), 0);
  const totalTokensOut = chartData.reduce((sum, data) => n(sum) + n(data.tokensOut), 0);
  const totalTokens = totalTokensIn + totalTokensOut;

  // Per-provider window totals: the drill-down report is authoritative when
  // loaded; the day series is the fallback so the table never blanks.
  const providerRows = useMemo(() => {
    const statsByKey = new Map(
      (providersReport?.providers || []).map((p) => [p.key, p]),
    );
    return AI_PROVIDERS.map((meta) => {
      const stats = statsByKey.get(meta.key) || null;
      const prompts = stats
        ? stats.totals.prompts
        : chartData.reduce((sum, day) => n(sum) + n(day[meta.key]), 0);
      const tokens = stats
        ? stats.totals.tokensIn + stats.totals.tokensOut
        : chartData.reduce(
            (sum, day) =>
              n(sum) + n(day?.tokensInByPlatform?.[meta.key]) + n(day?.tokensOutByPlatform?.[meta.key]),
            0,
          );
      return { meta, stats, prompts, tokens };
    });
  }, [providersReport, chartData]);

  const windowPrompts = providerRows.reduce((sum, row) => sum + row.prompts, 0);

  if (loading) {
    return (
      <Layout>
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  const streamTitle = isCompanyScope ? t`Team Activity Stream` : t`Activity Stream`;
  const canManageSubscription = !!account?.isAdmin && (isPro || isTrial);

  return (
    <Layout>
      {/* Payment failure banner (admins only) */}
      {paymentFailed && (
        <section
          className="rounded-xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-500/40"
          role="alert"
        >
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
              <Trans>Payment failed. Update your payment method to keep protection active.</Trans>
            </p>
          </div>
          <button
            onClick={() => navigate('/subscription')}
            className="self-start sm:self-auto px-4 py-2 text-sm font-semibold rounded-lg text-white bg-amber-600 hover:bg-amber-700 transition-colors whitespace-nowrap"
          >
            <Trans>Update payment method</Trans>
          </button>
        </section>
      )}

      {/* EPIC-15: free-plan value tease. States plainly what is protected and
          what passes through raw, and sells the upgrade. Never shown on Max. */}
      {!loading && !isMaxTier && (
        <section
          className="glass-panel rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
          style={{ border: '1px solid var(--color-max-border)' }}
        >
          <p className="text-sm mb-0" style={{ color: 'var(--color-text-secondary)' }}>
            <span className="font-semibold" style={{ color: 'var(--color-text)' }}><Trans>You are on Pro, free.</Trans></span>{' '}
            <Trans>ChatGPT, Claude, Gemini, Grok and Perplexity chats are protected. Cowork, Claude Code, Codex, Copilot, agents and API traffic pass through unprotected.</Trans>
          </p>
          <button
            onClick={() => setUpgradeOpen(true)}
            className="self-start sm:self-auto px-4 py-2 text-sm font-semibold rounded-lg text-white transition-all hover:shadow-lg whitespace-nowrap"
            style={{ backgroundColor: 'var(--color-max-accent)' }}
          >
            <Trans>Protect everything with Max</Trans>
          </button>
        </section>
      )}

      {/* Stats Grid */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 sm:p-6 glass-panel rounded-2xl">
          <p className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}><Trans>Total Prompts</Trans></p>
          <h2 className="text-2xl sm:text-3xl font-light">{totalPrompts.toLocaleString()}</h2>
          <div className="mt-4 h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-border)' }}>
            <div
              className="h-full bg-blue-500"
              style={{ width: `${Math.min((totalPrompts / 10000) * 100, 100)}%` }}
            ></div>
          </div>
        </div>

        <div className="p-5 sm:p-6 glass-panel rounded-2xl">
          <p className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}><Trans>Prompts This Month</Trans></p>
          <h2 className="text-2xl sm:text-3xl font-light">{currentMonthPrompts.toLocaleString()}</h2>
          <p className="text-[10px] mt-2" style={{ color: 'var(--color-text-muted)' }}>
            <Trans>{monthName}, month to date</Trans>
            {isCompanyScope ? (
              <>
                {' · '}
                <Plural value={scopeUserCount} one="# team member" other="# team members" />
              </>
            ) : ''}
          </p>
        </div>

        <div className="p-5 sm:p-6 glass-panel rounded-2xl">
          <p className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}>
            {effectiveUsageDays === 1 ? <Trans>Tokens (24h)</Trans> : <Trans>Tokens ({effectiveUsageDays}d)</Trans>}
          </p>
          <h2 className="text-2xl sm:text-3xl font-light">{totalTokens.toLocaleString()}</h2>
          <p className="text-[10px] mt-2" style={{ color: 'var(--color-text-muted)' }}>
            <Trans>{totalTokensIn.toLocaleString()} in · {totalTokensOut.toLocaleString()} out</Trans>
          </p>
        </div>

        <div
          className={`p-5 sm:p-6 glass-panel rounded-2xl border-l-4 sm:col-span-2 lg:col-span-1 ${isMax ? '' : 'border-indigo-500'}`}
          style={isMax ? { borderLeftColor: 'var(--color-max-accent)' } : undefined}
        >
          <p className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}><Trans>Current Plan</Trans></p>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            {isMax ? <Trans>Max Plan</Trans> : isPro ? (account?.subscriptionId ? <Trans>Pro Plan</Trans> : <Trans>Pro (Free)</Trans>) : isTrial ? <Trans>Trial</Trans> : <Trans>Inactive</Trans>}
            {isMax && <MaxBadge />}
          </h2>
          {account?.company && (
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              {account.company.name}
              {account.isAdmin && <span className="ml-1 text-blue-600 dark:text-blue-400"><Trans>(Admin)</Trans></span>}
            </p>
          )}
          {isTrial && (
            <div className="mt-2 space-y-1">
              {daysRemaining !== null && (
                <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                  <Plural value={daysRemaining} one="# day remaining" other="# days remaining" />
                </p>
              )}
            </div>
          )}
          {isPro && account?.subscriptionEndDate && (
            <p className="text-xs mt-2" style={{ color: 'var(--color-text-secondary)' }}>
              {account?.subscriptionStatus === 'canceled' ? (
                <Trans>Access until {new Date(account.subscriptionEndDate).toLocaleDateString()}</Trans>
              ) : (
                <Trans>Renews {new Date(account.subscriptionEndDate).toLocaleDateString()}</Trans>
              )}
            </p>
          )}
          {!isMax && (
            <button
              className="mt-2 text-xs font-bold hover:underline"
              style={{ color: 'var(--color-max-text)' }}
              onClick={() => setUpgradeOpen(true)}
            >
              <Trans>Upgrade to Max →</Trans>
            </button>
          )}
          {canManageSubscription && (
            <button
              className="mt-2 text-xs text-green-600 dark:text-green-400 font-bold hover:underline"
              onClick={() => navigate('/subscription')}
            >
              <Trans>Manage Subscription →</Trans>
            </button>
          )}
        </div>
      </section>

      {/* Desktop Chart */}
      <section className="hidden md:block p-6 lg:p-8 glass-panel rounded-3xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-sm font-semibold">{streamTitle}</h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
              <Trans>Protected prompts per day, by AI app</Trans>
              {isCompanyScope ? (
                <>
                  {' · '}
                  <Trans>across all {scopeUserCount} team members</Trans>
                </>
              ) : ''}
            </p>
          </div>
          <div
            className="inline-flex rounded-lg overflow-hidden text-xs"
            style={{ border: '1px solid var(--color-border)' }}
            role="group"
            aria-label={t`Activity stream range`}
          >
            {/* EPIC-15: the free tier gets 24h; the longer ranges stay
                visible but locked — clicking one opens the Max checkout.
                The server clamps regardless; these buttons are honest UI. */}
            {(isMaxTier
              ? [
                  { label: t`30d`, value: 30, locked: false },
                  { label: t`90d`, value: 90, locked: false },
                  { label: t`1y`, value: 365, locked: false },
                ]
              : [
                  { label: t`24h`, value: 1, locked: false },
                  { label: t`30d`, value: 30, locked: true },
                  { label: t`90d`, value: 90, locked: true },
                  { label: t`1y`, value: 365, locked: true },
                ]
            ).map((opt) => {
              const active = opt.locked ? false : effectiveUsageDays === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => (opt.locked ? setUpgradeOpen(true) : setUsageDays(opt.value))}
                  aria-pressed={active}
                  title={opt.locked ? t`Full usage history comes with Max` : undefined}
                  className="px-3 py-1.5 font-medium transition-colors inline-flex items-center gap-1"
                  style={{
                    backgroundColor: active ? 'var(--color-surface-secondary)' : 'transparent',
                    color: active ? 'var(--color-text)' : opt.locked ? 'var(--color-max-text)' : 'var(--color-text-muted)',
                  }}
                >
                  {opt.locked && (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  )}
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="h-72">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  {AI_PROVIDERS.map((p) => {
                    const color = providerColor(p, isDark);
                    return (
                      <linearGradient key={p.key} id={`stream-${p.key}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={color} stopOpacity={0.03} />
                      </linearGradient>
                    );
                  })}
                </defs>
                <CartesianGrid stroke="var(--chart-grid)" strokeWidth={1} vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={shortDate}
                  stroke="var(--chart-axis)"
                  tick={{ fill: 'var(--chart-axis)', fontSize: 11 }}
                  tickLine={false}
                  minTickGap={32}
                />
                <YAxis
                  allowDecimals={false}
                  stroke="var(--chart-axis)"
                  tick={{ fill: 'var(--chart-axis)', fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={36}
                />
                <Tooltip content={<StreamTooltip />} />
                <Legend wrapperStyle={{ fontSize: '0.72rem', paddingTop: '0.75rem' }} iconType="circle" iconSize={8} />
                {AI_PROVIDERS.map((p) => {
                  const color = providerColor(p, isDark);
                  return (
                    <Area
                      key={p.key}
                      type="monotone"
                      dataKey={p.key}
                      name={p.name}
                      stackId="providers"
                      stroke={color}
                      strokeWidth={2}
                      fill={`url(#stream-${p.key})`}
                      activeDot={{ r: 4, strokeWidth: 2, stroke: 'var(--color-surface)' }}
                    />
                  );
                })}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-sm" style={{ color: 'var(--color-text-muted)' }}>
              <Trans>No usage data yet</Trans>
            </div>
          )}
        </div>

        {/* Where the prompts came from: a phone or a computer. */}
        {surfaceTotals.length > 0 && (
          <div
            className="mt-4 pt-4 flex flex-wrap items-center gap-x-6 gap-y-3"
            style={{ borderTop: '1px solid var(--color-border)' }}
          >
            <span
              className="text-xs font-medium uppercase tracking-wide"
              style={{ color: 'var(--color-text-muted)' }}
            >
              <Trans>Protected from</Trans>
            </span>
            {surfaceTotals.map(({ meta, prompts }) => (
              <div key={meta.key} className="flex items-center gap-2">
                <span
                  className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                  style={{ backgroundColor: isDark ? meta.color.dark : meta.color.light }}
                  aria-hidden="true"
                />
                <span className="text-sm font-semibold tabular-nums">
                  {prompts.toLocaleString()}
                </span>
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {meta.label}
                  {surfacePromptTotal > 0
                    ? ` · ${Math.round((prompts / surfacePromptTotal) * 100)}%`
                    : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Mobile Insight Cards */}
      <section className="md:hidden space-y-3">
        <h3 className="text-sm font-semibold">
          {isCompanyScope ? <Trans>Team AI Activity</Trans> : <Trans>Your AI Activity</Trans>}
        </h3>
        {providerRows.map(({ meta, prompts, tokens }) => (
          <button
            key={meta.key}
            type="button"
            onClick={() => setReportProvider(meta)}
            className={`w-full text-left glass-panel rounded-xl p-4 flex items-center gap-4 border transition-transform active:scale-[0.99] ${meta.borderColor}`}
          >
            <div className={`w-11 h-11 rounded-lg ${meta.bgLight} flex items-center justify-center flex-shrink-0`}>
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: providerColor(meta, isDark) }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">{meta.name}</p>
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {meta.vendor} · {meta.tools.map((t) => t.name).join(' · ')}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-lg font-semibold tabular-nums">{prompts.toLocaleString()}</p>
              <p className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}><Trans>prompts</Trans></p>
              <p className="text-[10px] mt-1 tabular-nums" style={{ color: 'var(--color-text-muted)' }}>
                <Trans>{tokens.toLocaleString()} tokens</Trans>
              </p>
            </div>
          </button>
        ))}
      </section>

      {/* Desktop Table */}
      <section className="hidden md:block overflow-hidden glass-panel rounded-2xl">
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h3 className="text-sm font-semibold"><Trans>Supported AI Apps</Trans></h3>
          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            <Trans>Last {usageDays} days</Trans>
            {isCompanyScope ? (
              <>
                {' · '}
                <Trans>all {scopeUserCount} team members</Trans>
              </>
            ) : (
              <>
                {' · '}
                <Trans>your activity</Trans>
              </>
            )}
          </p>
        </div>
        <table className="w-full text-left">
          <thead style={{ backgroundColor: 'var(--color-surface-secondary)', borderBottom: '1px solid var(--color-border)' }}>
            <tr className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
              <th className="p-4"><Trans>AI App</Trans></th>
              <th className="p-4"><Trans>Provider</Trans></th>
              <th className="p-4"><Trans>Prompts</Trans></th>
              <th className="p-4"><Trans>Tokens</Trans></th>
              <th className="p-4"><Trans>Share</Trans></th>
              <th className="p-4"><Trans>Status</Trans></th>
              <th className="p-4 text-right"><Trans>Details</Trans></th>
            </tr>
          </thead>
          <tbody className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            {providerRows.map(({ meta, prompts, tokens }) => {
              const share = windowPrompts > 0 ? (prompts / windowPrompts) * 100 : 0;
              const active = prompts > 0 || tokens > 0;
              return (
                <tr key={meta.key} className="border-b transition-colors hover:bg-white/50 dark:hover:bg-white/5" style={{ borderColor: 'var(--color-border)' }}>
                  <td className="p-4 font-medium" style={{ color: 'var(--color-text)' }}>
                    <span className="inline-flex items-center gap-2.5">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: providerColor(meta, isDark) }} />
                      <span>
                        {meta.name}
                        <span className="block text-[10px] font-normal" style={{ color: 'var(--color-text-muted)' }}>
                          {meta.tools.map((t) => t.name).join(' · ')}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td className="p-4">{meta.vendor}</td>
                  <td className="p-4 tabular-nums">{prompts.toLocaleString()}</td>
                  <td className="p-4 tabular-nums">{tokens.toLocaleString()}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2 min-w-[110px]">
                      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-border)' }}>
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${Math.min(share, 100)}%`, backgroundColor: providerColor(meta, isDark) }}
                        />
                      </div>
                      <span className="text-xs tabular-nums w-9 text-right" style={{ color: 'var(--color-text-muted)' }}>
                        {share >= 1 || share === 0 ? Math.round(share) : '<1'}%
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    {active ? (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1 rounded-full text-[10px] font-semibold bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        <Trans>Active</Trans>
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1 rounded-full text-[10px] font-medium border"
                        style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--color-text-muted)', opacity: 0.5 }} />
                        <Trans>No activity</Trans>
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <button
                      type="button"
                      onClick={() => setReportProvider(meta)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors hover:bg-slate-100 dark:hover:bg-white/10 whitespace-nowrap"
                      style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" />
                      </svg>
                      <Trans>Details</Trans>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {reportProvider && (
        <ProviderDetailModal
          provider={reportProvider}
          stats={providersReport?.providers.find((p) => p.key === reportProvider.key) || null}
          days={effectiveUsageDays}
          scope={providersReport?.scope || 'user'}
          userCount={scopeUserCount}
          onClose={() => setReportProvider(null)}
        />
      )}

      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} onUpgraded={() => loadDashboardData()} source="dashboard" />
    </Layout>
  );
}
